import { useMemo } from "react";

import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Button, Divider, InputNumber, Space } from "antd";
import {
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_HIGHER_SCALER,
  DEFAULT_NORMALIZE_BY_LOWER_SCALER,
  DEFAULT_NORMALIZE_BY_THRESHOLD
} from "constants/chart.constants";
import styled from "styled-components";
import { getProductDisplayName } from "utils/data-table";

import useNormalizeFieldConversion from "hooks/useNormalizeFieldConversion";
import useUserUnit from "hooks/useUserUnit";

import { INormalizeBySetting } from "models";

import { LabeledSwitch } from "components/base";
import GroupBy from "components/groupBy";

interface NormalizeBySettingsProps {
  value?: INormalizeBySetting;
  onChange: (next: INormalizeBySetting) => void;
}

const NormalizeBySettings = ({ value, onChange }: NormalizeBySettingsProps) => {
  const unitType = useUserUnit();
  const normalizationSettings = useMemo(
    () => ({ ...DEFAULT_NORMALIZE_BY, ...(value ?? {}) }),
    [value]
  );

  const { displayName, field, per, unit, defaultUnit, useNormalizeBy, units } =
    normalizationSettings;

  const updateSettings = (next: INormalizeBySetting) => {
    onChange(next);
  };

  const fallbackDisplay = (displayName || DEFAULT_NORMALIZE_BY.displayName)
    .replace(/\s*\(.*?\)\s*$/g, "")
    .trim();
  const fallbackField = field || DEFAULT_NORMALIZE_BY.field;
  const fallbackUnit = unit || DEFAULT_NORMALIZE_BY.unit;
  const fallbackDefaultUnit = defaultUnit || DEFAULT_NORMALIZE_BY.defaultUnit;

  const { displayLabel, displayUnit } = useNormalizeFieldConversion({
    field: fallbackField,
    fallbackDisplay,
    fallbackUnit,
    units,
    defaultUnit: fallbackDefaultUnit
  });

  const displayLabelWithUnit = displayUnit
    ? `${displayLabel} (${displayUnit})`
    : displayLabel;

  const groupByValue = {
    property: fallbackField,
    title: displayLabelWithUnit
  };

  const handleFieldChange = (groupBy): void => {
    const { property, title, defaultUnit, normalizeByDefault, units, product } = groupBy;
    const formattedTitle = product
      ? `${getProductDisplayName(product)}: ${title}`
      : title;

    let convertedUnit = defaultUnit;
    if (units && units[unitType]) {
      convertedUnit = units[unitType];
    }

    const next: INormalizeBySetting = {
      ...normalizationSettings,
      displayName: formattedTitle,
      field: property,
      per: normalizeByDefault > 0 ? normalizeByDefault : 1,
      unit: convertedUnit,
      defaultUnit: defaultUnit,
      units: units,
      useMultilinearNormalization: false,
      threshold: DEFAULT_NORMALIZE_BY_THRESHOLD,
      lowerScalar: DEFAULT_NORMALIZE_BY_LOWER_SCALER,
      higherScalar: DEFAULT_NORMALIZE_BY_HIGHER_SCALER
    };

    updateSettings(next);
  };

  const handleToggle = (active: boolean) => {
    // When toggling normalization ON, resolve the unit from the units dictionary
    // if the current unit is empty (e.g. DEFAULT_NORMALIZE_BY has unit="").
    // This ensures the saved NormalizeBy.Unit is populated immediately.
    const resolvedUnit = active && !unit && units ? units[unitType] || "" : unit;
    updateSettings({
      ...normalizationSettings,
      useNormalizeBy: active,
      unit: resolvedUnit
    });
  };

  const handlePerChange = (value: number | null): void => {
    updateSettings({
      ...normalizationSettings,
      per: value ?? normalizationSettings.per
    });
  };

  const handlePerBlur = (): void => {
    const currentValue = normalizationSettings.per;
    if (currentValue == null || currentValue <= 0) {
      updateSettings({
        ...normalizationSettings,
        per: 1
      });
    }
  };

  return (
    <Wrapper>
      <StyledSpace direction="vertical">
        <LabeledSwitch
          testId="normalizeSwitch"
          switch={{
            isChecked: useNormalizeBy as boolean,
            onChange: handleToggle
          }}
          label={{
            value: "Normalize Feature",
            isBold: true,
            isUppercase: true
          }}
        />
        {useNormalizeBy && (
          <>
            <StyledDivider />
            <Row>
              <LeftField>
                <Label>Normalize Field</Label>
                <GroupBy
                  value={groupByValue}
                  dataTypeFilters={["Number", "Integer"]}
                  editable={false}
                  placement="rightTop"
                  onChange={handleFieldChange}>
                  <StyledButton block>
                    {displayLabelWithUnit}
                    <KeyboardArrowRightIcon style={{ fontSize: 24 }} />
                  </StyledButton>
                </GroupBy>
              </LeftField>

              <RightField>
                <Label>Per{displayUnit && ` (${displayUnit})`}</Label>
                <StyledNumberInput
                  data-testid="normalizePerInput"
                  value={per}
                  min={0}
                  controls={false}
                  onChange={handlePerChange}
                  onBlur={handlePerBlur}
                />
              </RightField>
            </Row>
          </>
        )}
      </StyledSpace>
    </Wrapper>
  );
};

export default NormalizeBySettings;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5%;
  margin-bottom: 10px;
`;

const LeftField = styled.div`
  width: 62%;
`;

const RightField = styled.div`
  width: 33%;
`;

const Label = styled.span`
  white-space: break-spaces;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  margin-top: 0;
`;

const StyledButton = styled(Button)`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  height: 30px;
  text-align: left;

  & > span {
    max-width: fit-content;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledNumberInput = styled(InputNumber)`
  width: 100%;
  height: 30px;
`;

const Wrapper = styled.div`
  width: 380px;
  max-width: 380px;
  background: var(--normalize-section-bg, #ffffff);
  font-weight: 500;
  padding: 20px 20px;
  text-align: left;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledSpace = styled(Space)`
  width: 100%;
`;
